import type { RaceState } from "./Races";

const API_BASE = "/api";

export type BattleStatus = "pending" | "active" | "settled" | "declined" | "canceled";

export interface Battle {
  id: string;
  challenger: string;
  opponent: string;
  bet: number;
  status: BattleStatus;
  winner: string | null;
  challengerScore: number | null;
  opponentScore: number | null;
}

/** A battle row plus the "me-centric" outcome returned after submitting a score. */
export interface BattleScoreResult extends Battle {
  settled: boolean;
  outcome?: "win" | "lose" | "tie";
  payout?: number;
}

/**
 * Async player-vs-player battles. A challenger bets flies, the opponent matches
 * the bet, both play one run, and the higher score takes the whole pot.
 *
 * `Battles` is a thin API client plus a one-shot `pendingBattleId` that flags the
 * next run as counting for a specific battle (consumed by GameOverScene).
 */
export class Battles {
  private list: Battle[] = [];
  /** Battle the next/just-finished run is scored against, then cleared. */
  pendingBattleId: string | null = null;

  all(): Battle[] {
    return this.list.slice();
  }

  /** Battles waiting on this player: a received challenge, or an active run to play. */
  actionableCount(myName: string): number {
    const me = myName.toLowerCase();
    return this.list.filter((b) => {
      if (b.status === "pending") return b.opponent.toLowerCase() === me;
      if (b.status === "active") {
        const isCh = b.challenger.toLowerCase() === me;
        return (isCh ? b.challengerScore : b.opponentScore) === null;
      }
      return false;
    }).length;
  }

  async refresh(name: string, token: string): Promise<void> {
    if (!name || !token) return;
    try {
      const res = await fetch(`${API_BASE}/battles?name=${encodeURIComponent(name)}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as unknown;
      if (Array.isArray(data)) this.list = data as Battle[];
    } catch {
      // leave cache in place on network errors
    }
  }

  async create(
    name: string,
    token: string,
    opponent: string,
    bet: number,
  ): Promise<{ ok: boolean; error?: string }> {
    return this.post("/battles", token, { name, opponent, bet });
  }

  async accept(name: string, token: string, id: string): Promise<{ ok: boolean; error?: string }> {
    return this.post(`/battles/${encodeURIComponent(id)}/accept`, token, { name });
  }

  /** Start (or join) the live race that settles an active battle. */
  async startRace(name: string, token: string, id: string): Promise<RaceState | null> {
    try {
      const res = await fetch(`${API_BASE}/battles/${encodeURIComponent(id)}/race`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) return null;
      return (await res.json()) as RaceState;
    } catch {
      return null;
    }
  }

  async cancel(name: string, token: string, id: string): Promise<{ ok: boolean; error?: string }> {
    return this.post(`/battles/${encodeURIComponent(id)}/cancel`, token, { name });
  }

  async submitScore(
    name: string,
    token: string,
    id: string,
    score: number,
  ): Promise<{ ok: boolean; result?: BattleScoreResult; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}/battles/${encodeURIComponent(id)}/score`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, score }),
      });
      const data = (await res.json().catch(() => ({}))) as BattleScoreResult & { error?: string };
      if (!res.ok) return { ok: false, error: data.error ?? "error" };
      return { ok: true, result: data };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  private async post(
    path: string,
    token: string,
    body: Record<string, unknown>,
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) return { ok: true };
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: data.error ?? "error" };
    } catch {
      return { ok: false, error: "network" };
    }
  }
}
